<div class="mobile-container">
  <!-- ヘッダー: ユーザー情報 + 総合スコア -->
  <div class="mobile-header">
    <div class="user-info">
      <span class="user-name">{{ user?.name }}</span>
      <span class="user-department">{{ user?.departmentName }}</span>
    </div>
    @if (
      assessment &&
      assessment.status === 'COMPLETED' &&
      assessment.skipEvaluation
    ) {
      <div class="overall-score">
        <span class="score-label">評価なし</span>
      </div>
    } @else if (assessment && assessment.status === 'COMPLETED') {
      <div class="overall-score">
        <span class="score-label">{{ overallScoreLabel }}</span>
        <span class="score-value">
          <!-- GRADE モード: overallGrade を表示。
          閾値未マッチで null になった場合は『—』プレースホルダで表示する
          (要件「具体的な点数を見せない」を遵守、admin 警告 UI で運用検知) -->
          @if (assessment.overallScoreLogicType === 'GRADE') {
            <span class="grade-display">{{
              assessment.overallGrade ?? '—'
            }}</span>
          } @else {
            @if (
              assessment.overallScoreLogicType === 'WEIGHTED' &&
              assessment.denominator != null
            ) {
              {{ assessment.overallScore | number: '1.0-0' }} /
              {{ assessment.denominator | number: '1.0-0' }}
            } @else {
              @if (assessment.overallScoreLogicType === 'WEIGHTED') {
                {{ assessment.overallScore | number: '1.0-0'
                }}<span class="score-suffix">点</span>
              } @else {
                {{ assessment.overallScore | number: '1.2-2' }}
              }
            }
          }
        </span>
      </div>
    }
  </div>

  @if (assessment && assessment.tags.length > 0) {
    <div class="mobile-tags" aria-label="付与済みタグ">
      @for (tag of assessment.tags; track tag.tagMasterId) {
        <span class="badge assessment-tag">{{ tag.name }}</span>
      }
    </div>
  }

  <!-- 商談セレクト -->
  <div class="meeting-select">
    <span class="select-label">商談</span>
    <app-select
      (selectedOptionChange)="assessmentChange.emit($event)"
      [options]="assessments"
      [selectedOption]="selectedAssessment"
    ></app-select>
  </div>

  <!-- ローディング -->
  @if (assessment && assessment.status === 'IN_PROGRESS') {
    <app-loading [showLoading]="true"></app-loading>
  }

  <!-- エラー -->
  @if (assessment && assessment.status === 'FAILED') {
    <div class="error">
      <strong>アセスメント中にエラーが発生しました</strong>
      <p>
        アップロードしたファイルに問題ないかご確認のうえ、再度お試しください。
      </p>
    </div>
  }

  <!-- メインコンテンツ（評価完了時） -->
  @if (assessment && assessment.status === 'COMPLETED') {
    <!-- KAフィルタ -->
    <div class="ka-filter">
      @if (assessment.businessMeeting.summary) {
        <button
          class="ka-button"
          [class.active]="showSummary"
          [attr.aria-pressed]="showSummary"
          (click)="summaryToggle.emit()"
        >
          <mat-icon class="material-icons-outlined">assignment</mat-icon>
          サマリー
        </button>
      }
      @if (!assessment.skipEvaluation) {
        @for (evaluation of keyActionEvaluations; track evaluation.id) {
          <button
            class="ka-button"
            [class.active]="
              !showSummary && selectedKeyAction?.id === evaluation.id
            "
            [attr.aria-pressed]="
              !showSummary && selectedKeyAction?.id === evaluation.id
            "
            (click)="keyActionSelect.emit(evaluation.id)"
          >
            {{ evaluation.keyAction.alias
            }}<span
              class="dot"
              [class.green]="isHighlight(evaluation)"
              [class.orange]="isLowLight(evaluation)"
              >●</span
            >
          </button>
        }
      }
    </div>
    <!-- サマリー表示 -->
    @if (showSummary) {
      <div class="evaluation-card">
        <app-ai-badge></app-ai-badge>
        <div class="summary-section">
          <div class="summary-header">
            <span class="summary-title">商談の背景</span>
            <button
              type="button"
              class="copy-button"
              (click)="copyToClipboard(assessment.businessMeeting.summary)"
            >
              <mat-icon class="material-icons-outlined">file_copy</mat-icon>
            </button>
          </div>
          <div
            class="summary-content"
            [innerHTML]="assessment.businessMeeting.summary | markdown"
          ></div>
        </div>
      </div>
    }
    <!-- KA評価表示 -->
    @if (!showSummary && selectedKeyAction && !assessment.skipEvaluation) {
      <div class="evaluation-card">
        <app-ai-badge></app-ai-badge>
        <div class="ka-header">
          <div class="ka-info">
            <span class="ka-alias">{{
              selectedKeyAction.keyAction.alias
            }}</span>
            @if (getScoreLabel(selectedKeyAction)) {
              <span
                class="score-badge"
                [class.highlight]="isHighlight(selectedKeyAction)"
                [class.lowlight]="isLowLight(selectedKeyAction)"
              >
                {{ getScoreLabel(selectedKeyAction) }}
              </span>
            }
          </div>
          @if (selectedKeyAction.score != null) {
            <span class="ka-score">
              {{ selectedKeyAction.score | number: '1.2-2' }}
            </span>
          }
        </div>
        <div class="ka-name">{{ selectedKeyAction.keyAction.name }}</div>
        <div
          class="evaluation-content"
          [innerHTML]="selectedKeyAction.evaluation | markdown"
        ></div>
        <!-- おすすめコンテンツ -->
        @if (selectedKeyAction.recommendedContents?.length) {
          <div class="recommendation">
            <div class="recommendation-badge">おすすめコンテンツ</div>
            @for (rec of selectedKeyAction.recommendedContents; track rec.id) {
              <div class="recommendation-item">
                <a
                  class="recommendation-link"
                  [href]="rec.content.url"
                  (click)="contentAccessed.emit(rec.id)"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ rec.content.name }}
                </a>
                <mat-icon>{{
                  rec.isAccessed ? 'check_circle_filled' : 'play_circle_outline'
                }}</mat-icon>
              </div>
            }
          </div>
        }
        <!-- ロールモデル商談 -->
        @if (
          selectedKeyAction.roleModelAssessments?.length &&
          adminOrg?.useRoleModelRecommendation
        ) {
          <div class="recommendation">
            <div class="recommendation-badge">ロールモデル商談</div>
            @for (rm of selectedKeyAction.roleModelAssessments; track rm.id) {
              <div class="recommendation-item">
                <a
                  class="recommendation-link"
                  [routerLink]="['/home']"
                  [queryParams]="{
                    'assessment-id': rm.id,
                    'key-action-id': selectedKeyAction.keyAction.id,
                    'user-id': rm.user.id,
                  }"
                  target="_blank"
                >
                  {{ selectedKeyAction.keyAction.alias }}が得意な{{
                    rm.user.name
                  }}さんの商談
                </a>
              </div>
            }
          </div>
        }
      </div>
    }
    <!-- 文字起こし -->
    <div class="transcript-card">
      @if (downloadUrl && assessment.status === 'COMPLETED') {
        <div class="media-space">
          <audio controls preload="none">
            <source
              src="{{ downloadUrl }}"
              type="{{ getAudioMimeType(downloadUrl) }}"
            />
          </audio>
        </div>
      }
      <div class="transcript-title">
        <span>文字起こし</span>
        @if (canDownloadTranscript()) {
          <button
            type="button"
            class="transcript-download-button"
            aria-label="文字起こしをテキストでダウンロード"
            title="文字起こしをテキストでダウンロード"
            (click)="transcriptDownload.emit()"
          >
            <mat-icon class="material-icons-outlined">download</mat-icon>
          </button>
        }
      </div>
      <div class="transcript-content">
        @for (speak of assessment.businessMeeting.transcript; track $index) {
          <div class="speak">
            <div class="speak-info">
              <span class="speaker">{{
                speak.speaker | speakerLabel: speakerLabelValues
              }}</span>
              <span class="speaked-at">{{ speak.speakedAt }}</span>
            </div>
            @if (speak.content) {
              <div class="content-info">
                @if (speak.content.assessment) {
                  <span
                    class="assessment-badge"
                    [attr.data-level]="speak.content.assessment"
                  >
                    {{ speak.content.assessment }}
                  </span>
                }
                @if (speak.content.reason) {
                  <span class="reason">
                    {{ speak.content.reason }}
                  </span>
                }
                @if (speak.content.reasonDetail) {
                  <span class="reason-detail">
                    {{ speak.content.reasonDetail }}
                  </span>
                }
              </div>
            }
            <div class="quote">{{ speak.quote }}</div>
          </div>
        }
      </div>
    </div>
    <!-- カテゴリ別スコア -->
    @if (categoryScores.length > 0 && !assessment.skipEvaluation) {
      <div class="category-section">
        <div class="section-title">カテゴリ別スコア</div>
        <div class="category-list">
          @for (item of categoryScores; track $index) {
            <div class="category-item">
              <span class="category-name">{{ item.category }}</span>
              <span class="category-score">{{
                item.average | number: '1.2-2'
              }}</span>
            </div>
          }
        </div>
      </div>
    }
  }
</div>
